import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import TrustedTraderBadge from "../../components/TrustedTraderBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import {
    ShoppingBag, Search, Star, Store, Truck, CheckCircle2, Clock,
    XCircle, MinusCircle, PlusCircle, Receipt, Sparkles,
} from "lucide-react";

const ORDER_STATUS = {
    pending_fulfilment: { bg: "bg-amber-50 text-amber-700 border-amber-200", labelKey: "shop.order.pending_fulfilment", icon: Clock },
    fulfilled:          { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", labelKey: "shop.order.fulfilled", icon: CheckCircle2 },
    cancelled:          { bg: "bg-rose-50 text-rose-700 border-rose-200", labelKey: "shop.order.cancelled", icon: XCircle },
};

export default function Marketplace() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [tab, setTab] = useState("shop");
    const [products, setProducts] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [orders, setOrders] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [search, setSearch] = useState("");
    const [cart, setCart] = useState({}); // { product_id: { product, qty } }
    const [activeMerchantId, setActiveMerchantId] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewItem, setReviewItem] = useState(null);

    const load = async () => {
        const [p, m, o, s] = await Promise.all([
            api.get(`/commerce/marketplace${search ? `?q=${encodeURIComponent(search)}` : ""}`),
            api.get("/commerce/merchants"),
            api.get("/commerce/orders"),
            api.get("/commerce/suppliers"),
        ]);
        setProducts(p.data);
        setMerchants(m.data);
        setOrders(o.data);
        setSuppliers(s.data);
    };
    useEffect(() => { load(); }, [search]);

    const addToCart = (p) => {
        if (activeMerchantId && activeMerchantId !== p.merchant_id) {
            if (!confirm("Switching merchants will clear your current cart. Proceed?")) return;
            setCart({});
        }
        setActiveMerchantId(p.merchant_id);
        setCart((c) => ({
            ...c,
            [p.id]: { product: p, qty: (c[p.id]?.qty || 0) + 1 },
        }));
        toast.success(`Added ${p.name} to cart`);
    };

    const changeQty = (pid, delta) => {
        setCart((c) => {
            const cur = c[pid];
            if (!cur) return c;
            const next = cur.qty + delta;
            if (next <= 0) {
                const { [pid]: _, ...rest } = c;
                if (Object.keys(rest).length === 0) setActiveMerchantId(null);
                return rest;
            }
            return { ...c, [pid]: { ...cur, qty: next } };
        });
    };

    const cartItems = Object.values(cart);
    const cartTotal = cartItems.reduce((s, ci) => s + ci.product.price * ci.qty, 0);

    const checkout = async () => {
        if (!activeMerchantId || cartItems.length === 0) return;
        try {
            await api.post("/commerce/orders", {
                merchant_id: activeMerchantId,
                items: cartItems.map((ci) => ({ product_id: ci.product.id, qty: ci.qty })),
            });
            toast.success("Order placed. The merchant will fulfil shortly.");
            setCart({});
            setActiveMerchantId(null);
            setTab("orders");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const openReview = (item) => {
        setReviewItem(item);
        setReviewOpen(true);
    };

    return (
        <div data-testid="marketplace-page">
            <MobileHeader title={t("shop.title")} backTo="/consumer" variant="blue">
                <p className="text-xs text-white/80 mt-3">{t("shop.subtitle", { merchants: merchants.length, suppliers: suppliers.length })}</p>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-4 w-full" data-testid="marketplace-tabs">
                        <TabsTrigger value="shop" data-testid="tab-shop"><ShoppingBag size={14} /> {t("shop.tab.shop")}</TabsTrigger>
                        <TabsTrigger value="merchants" data-testid="tab-merchants"><Store size={14} /> {t("shop.tab.merchants")}</TabsTrigger>
                        <TabsTrigger value="suppliers" data-testid="tab-suppliers"><Truck size={14} /> {t("shop.tab.suppliers")}</TabsTrigger>
                        <TabsTrigger value="orders" data-testid="tab-orders"><Receipt size={14} /> {t("shop.tab.orders")} ({orders.length})</TabsTrigger>
                    </TabsList>
                </Tabs>

                {tab === "shop" && (
                    <>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("shop.search")} className="pl-9 h-11 rounded-xl bg-white" data-testid="shop-search" />
                        </div>
                        {products.length === 0 && <Empty msg={t("shop.empty.search")} />}
                        <div className="grid grid-cols-2 gap-3">
                            {products.map((p) => <ProductCard key={p.id} p={p} cartQty={cart[p.id]?.qty || 0} onAdd={() => addToCart(p)} onPlus={() => changeQty(p.id, 1)} onMinus={() => changeQty(p.id, -1)} />)}
                        </div>
                    </>
                )}

                {tab === "merchants" && (
                    <div className="space-y-3">
                        {merchants.length === 0 && <Empty msg={t("shop.empty.merchants")} />}
                        {merchants.map((m) => <MerchantCard key={m.id} m={m} />)}
                    </div>
                )}

                {tab === "suppliers" && (
                    <div className="space-y-3">
                        {suppliers.length === 0 && <Empty msg={t("shop.empty.suppliers")} />}
                        {suppliers.map((s) => <SupplierCard key={s.id} s={s} />)}
                    </div>
                )}

                {tab === "orders" && (
                    <div className="space-y-3">
                        {orders.length === 0 && <Empty msg={t("shop.empty.orders")} />}
                        {orders.map((o) => <OrderCard key={o.id} o={o} onReload={load} onReview={openReview} />)}
                    </div>
                )}
            </div>

            {/* Floating cart button */}
            {cartItems.length > 0 && tab === "shop" && (
                <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-30">
                    <Button onClick={checkout} className="h-14 px-6 rounded-full bg-[#0066FF] hover:bg-[#0052cc] shadow-xl font-semibold" data-testid="checkout-btn">
                        <Sparkles size={16} /> {t("shop.checkout", { items: cartItems.reduce((s, ci) => s + ci.qty, 0), total: fmtCurrency(cartTotal) })}
                    </Button>
                </div>
            )}

            <ReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} item={reviewItem} onDone={load} />
        </div>
    );
}

function Empty({ msg }) {
    return <div className="bg-white rounded-2xl p-8 text-center text-sm text-slate-500 border border-slate-200/60 product-shadow">{msg}</div>;
}

function ProductCard({ p, cartQty, onAdd, onPlus, onMinus }) {
    const { t } = useI18n();
    return (
        <div className="bg-white rounded-2xl border border-slate-200/60 product-shadow overflow-hidden" data-testid={`product-${p.id}`}>
            <div className="h-24 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
                <ShoppingBag size={32} className="text-[#0066FF]/40" />
            </div>
            <div className="p-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">{p.merchant_name}</div>
                <div className="font-display font-semibold text-sm text-[#0A1930] truncate">{p.name}</div>
                <div className="flex items-center justify-between">
                    <div className="font-mono font-bold text-[#0066FF]">{fmtCurrency(p.price)}</div>
                    {p.rating != null && (
                        <div className="text-[10px] text-amber-600 flex items-center gap-0.5">
                            <Star size={10} fill="currentColor" /> {p.rating} ({p.review_count})
                        </div>
                    )}
                </div>
                <div className="text-[10px] text-slate-400">{p.stock} {t("shop.left")}</div>
                {cartQty === 0 ? (
                    <Button onClick={onAdd} size="sm" className="w-full h-8 rounded-lg text-xs bg-[#0066FF] hover:bg-[#0052cc]" data-testid={`add-${p.id}`}>
                        <ShoppingBag size={12} /> {t("shop.add")}
                    </Button>
                ) : (
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg px-1 py-0.5">
                        <button onClick={onMinus} className="text-[#0066FF]" data-testid={`minus-${p.id}`}><MinusCircle size={20} /></button>
                        <span className="text-sm font-bold">{cartQty}</span>
                        <button onClick={onPlus} className="text-[#0066FF]" data-testid={`plus-${p.id}`}><PlusCircle size={20} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}

function MerchantCard({ m }) {
    const { t } = useI18n();
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`merchant-${m.id}`}>
            <div className="flex items-start gap-3">
                <div className="size-12 rounded-xl flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br from-[#0066FF] to-[#0044CC]">
                    <Store size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{m.business_name}</div>
                        {m.is_trusted_trader && <TrustedTraderBadge size="xs" />}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {t("shop.merchant.products", { n: m.product_count })} · {m.category || "general"} · {m.country}
                    </div>
                    {m.credit_score && (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold">
                            <Star size={10} fill="currentColor" /> Credit score {m.credit_score}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SupplierCard({ s }) {
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`supplier-${s.id}`}>
            <div className="flex items-start gap-3">
                <div className="size-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br from-cyan-500 to-cyan-700">
                    <Truck size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#0A1930] truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{s.category || "supplier"} · {s.phone || "—"}</div>
                    <div className="text-[10px] text-slate-400 mt-1">Sourced by {s.sourced_by} · {s.country}</div>
                </div>
            </div>
        </div>
    );
}

function OrderCard({ o, onReload, onReview }) {
    const { t } = useI18n();
    const meta = ORDER_STATUS[o.status] || ORDER_STATUS.pending_fulfilment;
    const Icon = meta.icon;
    const cancel = async () => {
        if (!confirm("Cancel this order? You'll be refunded.")) return;
        try {
            await api.post(`/commerce/orders/${o.id}/cancel`);
            toast.success("Order cancelled, refund issued");
            onReload();
        } catch (err) { toast.error("Failed"); }
    };
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid={`order-${o.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <div className={`size-10 rounded-xl flex items-center justify-center ${meta.bg}`}><Icon size={18} /></div>
                    <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{o.merchant_name}</div>
                        <div className="text-[11px] text-slate-500">{o.items.length} item{o.items.length > 1 ? "s" : ""} · {fmtDate(o.created_at)}</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono font-bold">{fmtCurrency(o.total)}</div>
                    <Badge className={`mt-1 text-[10px] capitalize border ${meta.bg}`}>{t(meta.labelKey)}</Badge>
                </div>
            </div>
            <div className="mt-2 text-xs text-slate-600 truncate">
                {o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}
            </div>
            {o.status === "pending_fulfilment" && (
                <Button onClick={cancel} variant="outline" size="sm" className="w-full mt-3 h-8 rounded-lg text-xs border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`cancel-${o.id}`}>
                    {t("shop.order.cancel")}
                </Button>
            )}
            {o.status === "fulfilled" && (
                <div className="mt-3 flex gap-2 flex-wrap">
                    {o.items.map((i) => (
                        <Button key={i.product_id} onClick={() => onReview({ ...i, order_id: o.id })} size="sm" variant="outline" className="h-7 text-[10px] rounded-md" data-testid={`review-${i.product_id}-${o.id}`}>
                            <Star size={10} /> {t("shop.review.write", { name: i.name })}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReviewDialog({ open, onClose, item, onDone }) {
    const { t } = useI18n();
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/commerce/reviews", { product_id: item.product_id, rating, comment });
            toast.success("Review posted");
            setComment(""); setRating(5);
            onDone(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-2xl max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2"><Star size={18} className="text-amber-500" /> {t("shop.review.title", { name: item?.name || "" })}</DialogTitle>
                    <DialogDescription>{t("shop.review.desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                                <Star size={28} className={n <= rating ? "text-amber-500" : "text-slate-300"} fill={n <= rating ? "currentColor" : "none"} />
                            </button>
                        ))}
                    </div>
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("shop.review.comment")} rows={3} className="rounded-xl" data-testid="review-comment-input" />
                    <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-white" data-testid="review-submit-btn">
                        {busy ? "…" : t("shop.review.submit")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
